import React, { FC, memo, useCallback, useContext, useMemo } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import cn from "classnames";
import Button, { ButtonTypeEnum } from "@components/new/Button/Button";
import SectionHeader from "@components/new/SectionHeader/SectionHeader";
import useBrands from "@core/apiHooks/useBrands";
import BrandsAttributes from "./BrandsAttributes/BrandsAttributes";
import BrandsParties from "./BrandsParties/BrandsParties";
import BrandsFunnel from "./BrandsFunnel/BrandsFunnel";
import BrandsDetailedAttributes from "./BrandsDetailedAttributes/BrandsDetailedAttributes";
import SectionContent from "@components/new/SectionContent/SetionContent";
import { ReportsNameEnum } from "@models/ChainDetails";
import "./Brands.styles.scss";
import { isAvailable, PermissionLevelEnum } from "@models/PermissionSection";
import { ChainDetailsDataContext, ChainSectionsEnum } from "../../ChainPage.helpers";
import { useSectionNoAccessMessage } from "../../hooks/useSectionNoAccessMessage";
import { sanitizeUrl } from "@helpers";
import { ChainType } from "@models/Brands";
import { AppId, usePlatformContext } from "@datassential/platform-ui-lib";
import { requestTrialByFeature } from "@actions/page";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";

const Brands: FC = () => {
    const {
        data: {
            mainData: {
                id: chainId,
            },
            reportsData,
        },
        downloadReports,
    } = useContext(ChainDetailsDataContext);
    const { userInfo: { products} } = usePlatformContext();
    const dispatch = useDispatch();
    const noAccessMessage = useSectionNoAccessMessage(ChainSectionsEnum.Brand);

    const {
        isLoading,
        data: {
            permissionLevel,
            data,
        },
    } = useBrands(chainId);

    const {
        chainType,
    } = data;

    const brandPerformance = useMemo(
        () => products[AppId.BrandPerformance],
        [products]
    );
    const fingerprintsLink = brandPerformance?.url ?? '';
    const fingerprintsViewMoreLink = useMemo(() => {
        let typePath = "restaurants";

        switch (chainType) {
            case ChainType.Supermarket: typePath = "grocery"; break;
            case ChainType.CStore: typePath = "c-stores"; break;
        }
        return fingerprintsLink ? sanitizeUrl(`${fingerprintsLink}/${typePath}/chain/${chainId}/overview`) : ""
    }, [chainId, fingerprintsLink, chainType]);

    const report = reportsData.find(i => i.data.name === ReportsNameEnum.BrandFingerprints);
    const isReportDownloadAvailable = !!report && isAvailable(report);
    const isLocked = permissionLevel === PermissionLevelEnum.None;

    const requestTrial = useCallback(() => {
        toast.info("Request has been sent");
        dispatch(requestTrialByFeature("brandPerformances"));
    }, [dispatch]);

    return (
        <div className={cn("Brands", isLocked && "is-blocked")}>
            <SectionHeader title="Brand">
                <div className="Brands__actions-item">
                    {isReportDownloadAvailable && (
                        <Button
                            modifiers={["primary"]}
                            onClick={() => downloadReports([ReportsNameEnum.BrandFingerprints])}
                        >
                            DOWNLOAD BRAND REPORT
                        </Button>
                    )}
                </div>
            </SectionHeader>
            <SectionContent isLoading={isLoading}>
                <div className="Brands__content">
                    {data && (
                        <>
                            <div className="Brands__section">
                                <BrandsAttributes data={data}/>
                            </div>

                            <div className="Brands__section is-half">
                                <BrandsParties data={data}/>
                            </div>

                            {data.brandFunnelData && (
                                <div className="Brands__section is-full">
                                    <BrandsFunnel data={data}/>
                                </div>
                            )}

                            <div className="Brands__section is-sidebar">
                                <BrandsDetailedAttributes data={data} />
                            </div>

                            {!!fingerprintsLink && (
                                <div className="Brands__footer">
                                    <Button
                                        type={ButtonTypeEnum.Anchor}
                                        className="Brands__footer-action"
                                        link={fingerprintsViewMoreLink}
                                    >
                                        View more
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {isLocked && (
                        <PermissionOverlay
                            onContactUsClick={requestTrial}
                            testId="blockChainBrandPermissionOverlay"
                        >
                            <div>{noAccessMessage}</div>
                        </PermissionOverlay>
                    )}
                </div>
            </SectionContent>
        </div>
    );
};

export default memo(Brands);
